import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import BillingInvoices from '../components/BillingInvoices';
import ContractorForm from '../components/ContractorForm';
import { useConfirm } from '../components/ConfirmDialog';
import { useFeature } from '../lib/features';
import ContractsList from './contracts/ContractsList';
import ContractDetail from './contracts/ContractDetail';
import './ContractorsList.css';

/*
 * `view` 'contracts' is the third tab below, Contracts (120), which has an
 * address of its own - /manage/contracts, and /manage/contracts/:id for one
 * contract - so the bell's links and the back button land on it. The other
 * two tabs are this page's own state, as they always were; from the
 * contracts' address they go back to /manage/contractors on the one chosen.
 */
const ContractorsList = ({ view = null }) => {
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: contractId } = useParams();
  const contractsOn = useFeature('contracts');
  const [tab, setTab] = useState(view === 'contracts' ? 'contracts' : (location.state?.tab === 'billing' ? 'billing' : 'people'));
  const [contractors, setContractors] = useState([]);
  const [filteredContractors, setFilteredContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  /**
   * Money as the database actually sends it.
   *
   * hourly_rate is NUMERIC, and node-postgres returns NUMERIC as a string to
   * avoid losing precision - so `contractor.hourly_rate?.toFixed(2)` was
   * calling a method strings do not have. Optional chaining does not help:
   * it guards a null rate, and then throws on a real one. This page therefore
   * crashed outright for any contractor who had a rate set, which is most of
   * the point of having them.
   */
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `\u20ac${n.toFixed(2)}` : '-';
  };

  /**
   * Whether this contractor can open the portal, in the words the list needs.
   *
   * Reported by the API as portal_access, using the same test the sign-in
   * middleware applies - so this says what will actually happen at the door,
   * not what the record looks like.
   */
  const access = (c) => {
    if (c.availability_status === 'inactive') return { label: 'Inactive', tone: 'muted' };
    if (c.portal_access) return { label: 'Can sign in', tone: 'ok' };
    if (c.portal_access_ever) return { label: 'Expired', tone: 'warn' };
    return { label: 'No access', tone: 'warn' };
  };

  // Fetch contractors
  useEffect(() => {
    const fetchContractors = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/contractors`);
        setContractors(response.data || []);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load contractors');
        console.error('Error fetching contractors:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContractors();
  }, [API_URL]);

  // Filter contractors
  useEffect(() => {
    let filtered = contractors;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.company?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.availability_status === statusFilter);
    }

    if (companyFilter !== 'all') {
      filtered = filtered.filter(c => (c.company || '') === companyFilter);
    }

    setFilteredContractors(filtered);
  }, [contractors, searchTerm, statusFilter, companyFilter]);

  // Options for the company filter, taken from the data rather than hardcoded.
  const companies = [...new Set(contractors.map((c) => c.company).filter(Boolean))].sort();

  // Handle create contractor. The same form, and the same eleven fields, as
  // Settings -> Contractors (components/ContractorForm.js): this used to be a
  // five-field copy, so a contractor added here had no billing rate, skills
  // or availability until somebody edited them again.
  const handleCreateContractor = async (values) => {
    try {
      const response = await axios.post(`${API_URL}/contractors`, values);
      setContractors([...contractors, response.data]);
      setShowCreateForm(false);
      setError(null);
    } catch (err) {
      console.error('Error creating contractor:', err);
      throw new Error(err.response?.data?.error || 'Failed to create contractor');
    }
  };

  // Handle delete contractor
  const handleDeleteContractor = async (contractorId) => {
    if (await confirm({ title: 'Are you sure you want to deactivate this contractor?', confirmLabel: 'Deactivate', danger: true })) {
      try {
        await axios.delete(`${API_URL}/contractors/${contractorId}`);
        setContractors(contractors.filter(c => c.id !== contractorId));
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to deactivate contractor');
        console.error('Error deleting contractor:', err);
      }
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'status-available';
      case 'unavailable':
        return 'status-unavailable';
      case 'on-leave':
        return 'status-onleave';
      case 'inactive':
        return 'status-inactive';
      case 'busy':
        return 'status-busy';
      default:
        return 'status-unknown';
    }
  };

  // Get rating display
  const getRatingDisplay = (rating) => {
    if (!rating) return 'N/A';
    return `${rating.toFixed(1)}/5.0`;
  };

  if (loading && contractors.length === 0) {
    return <div className="contractors-list loading">Loading contractors...</div>;
  }

  return (
    <div className="contractors-list">
      <div className="list-header">
        <h1>Contractor Management</h1>
        {/* Adding a contractor belongs to the list, not to the invoices. */}
        {tab === 'people' && (
          <button
            className="btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ Add Contractor'}
          </button>
        )}
      </div>

      {/* Two views of the same people: who they are, and what they have
          invoiced. Tabs rather than a second item in the navigation bar -
          billing is a question you ask about contractors, so it belongs behind
          the word "Contractors" rather than beside it. */}
      <div className="cl-tabs" role="tablist">
        {[['people', 'Contractors'], ['billing', 'Billing'],
          ...(contractsOn || tab === 'contracts' ? [['contracts', 'Contracts']] : [])].map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? 'cl-tab is-active' : 'cl-tab'}
            onClick={() => {
              if (id === 'contracts') navigate('/manage/contracts');
              else if (view === 'contracts') navigate('/manage/contractors', { state: { tab: id } });
              else setTab(id);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'billing' && <BillingInvoices />}

      {/* Contracts (120): the register, or one contract when the address names it. */}
      {tab === 'contracts' && (contractId ? <ContractDetail key={contractId} /> : <ContractsList />)}

      {tab === 'people' && (
       <>
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {showCreateForm && (
        <div className="create-form-container">
          <ContractorForm title="Add a contractor" onSubmit={handleCreateContractor}
                          onCancel={() => setShowCreateForm(false)} />
        </div>
      )}

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search by name, email, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="toolbar-search"
          aria-label="Search contractors"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="toolbar-select"
          aria-label="Filter by availability"
        >
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="busy">Busy</option>
          <option value="unavailable">Unavailable</option>
          <option value="on-leave">On Leave</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="toolbar-select"
          aria-label="Filter by company"
        >
          <option value="all">All Companies</option>
          {companies.map((company) => (
            <option key={company} value={company}>{company}</option>
          ))}
        </select>
        {(searchTerm !== '' || statusFilter !== 'all' || companyFilter !== 'all') && (
          <button
            className="btn btn-secondary btn-small"
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); setCompanyFilter('all'); }}
          >
            Clear
          </button>
        )}
      </div>

      {filteredContractors.length > 0 ? (
        <div className="contractors-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Hourly Rate</th>
                <th>Status</th>
                <th>Portal</th>
                <th>Rating</th>
                <th>Hours</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContractors.map(contractor => (
                <tr key={contractor.id}>
                  <td data-label="Name">
                    <strong>{contractor.name}</strong>
                    {/* Said on the row, because the two kinds of worker look
                        identical here otherwise and the difference decides
                        whether their approved hours ever reach an invoice.
                        Set in Settings, on the user record, by whoever ticked
                        the work-abilities box. */}
                    {contractor.invoices === false && (
                      <span className="cl-internal" title="Their approved hours are costed, never invoiced">
                        Internal
                      </span>
                    )}
                  </td>
                  <td data-label="Email">{contractor.email}</td>
                  <td data-label="Company">{contractor.company || '-'}</td>
                  <td data-label="Hourly Rate">
                    {money(contractor.hourly_rate)}
                    {contractor.invoices === false && (
                      <span className="cl-rate-note">cost</span>
                    )}
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge ${getStatusColor(contractor.availability_status)}`}>
                      {contractor.availability_status || 'available'}
                    </span>
                  </td>
                  <td data-label="Portal">
                    <span className={`portal-badge portal-${access(contractor).tone}`}>
                      {access(contractor).label}
                    </span>
                    {/* The number that makes it urgent. Somebody with no
                        access and no tickets is merely not set up; somebody
                        with no access and four tickets has work they cannot
                        see. */}
                    {!contractor.portal_access
                      && contractor.availability_status !== 'inactive'
                      && contractor.ticket_count > 0 && (
                      <span className="portal-note">
                        {contractor.ticket_count} ticket{contractor.ticket_count === 1 ? '' : 's'} unseen
                      </span>
                    )}
                  </td>
                  <td data-label="Rating">
                    <span className="rating">
                      {getRatingDisplay(contractor.average_rating)}
                    </span>
                  </td>
                  <td data-label="Hours">{contractor.total_hours_worked || 0}</td>
                  <td data-label="Actions">
                    <div className="actions">
                      <Link
                        to={`/contractor-profile/${contractor.id}`}
                        className="btn-small"
                      >
                        View
                      </Link>
                      <button
                        className="btn-small btn-danger"
                        onClick={() => handleDeleteContractor(contractor.id)}
                      >
                        Deactivate
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-contractors">
          <p>No contractors found</p>
          {contractors.length === 0 ? (
            <p>Click "Add Contractor" to create your first contractor</p>
          ) : (
            <p>Try adjusting your search filters</p>
          )}
        </div>
      )}

      {/* Under the list, not above it: on a phone a line above the table
          pushed the people below the fold. Nothing here hands out access -
          a contractor's way in is the email address on their record: the
          front door sends a code to it. */}
      <p className="cl-signin-note">
        Contractors sign in at /login with a code emailed to them.
      </p>

      <div className="list-summary">
        <p>Showing {filteredContractors.length} of {contractors.length} contractors</p>
      </div>
       </>
      )}
      {confirmDialog}
    </div>
  );
};

export default ContractorsList;
